// Closed worlds (Ben, Oct 19 2026): the game forgets a world once it ends, so the Worker keeps each player's last numbers and
// each world's top five guilds and players (worker/history.ts), with Valor Tools' archive behind it. GET /api/history.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClosedWorld } from "@/game/history";
export type { ClosedWorld, Standing } from "@/game/history";

/** The Worker answers from what it has saved and, when that is stale, reads the game afterwards (`refreshing`): asked again a
 *  little later, a few times, so a world found by that read shows without leaving the Portal. */
const AGAIN_MS = 8000, TRIES = 4;

type HistoryData = { worlds: ClosedWorld[]; refreshing: boolean; tries: number };

export function useClosedWorlds() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["history"],
    queryFn: async (): Promise<HistoryData> => {
      const prev = qc.getQueryData<HistoryData>(["history"]);
      const r = await fetch("/api/history", { credentials: "same-origin", headers: { accept: "application/json" } });
      if (!r.ok) return { worlds: [], refreshing: false, tries: 0 };
      const j = (await r.json()) as { worlds?: ClosedWorld[]; refreshing?: boolean };
      const tries = prev?.refreshing || j.refreshing ? (prev?.tries ?? 0) + 1 : 0;
      return { worlds: Array.isArray(j.worlds) ? j.worlds : [], refreshing: !!j.refreshing || (!!prev?.refreshing && tries < TRIES), tries };
    },
    staleTime: 10 * 60_000,
    retry: 1,
    refetchInterval: (query) => (query.state.data?.refreshing && (query.state.data.tries ?? 0) < TRIES ? AGAIN_MS : false),
  });
  return { ...q, data: q.data?.worlds };
}
